import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from 'reduxStore/auth/slice';
import { defaultSettingsActions } from 'reduxStore/defaultSettings/slice';
import { organisationActions } from 'reduxStore/organisations/slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { Loader } from 'zComponents/atoms';
import { Row } from 'zui/Antd';

const appStateInfoMap = {
	loginLoading: 'Profile',
	profileLoading: 'Profile',
	organisationLoading: 'Workspace',
	orgSettingsLoading: 'Workspace Settings',
	personSettingsLoading: 'Person Settings',
	personDefaultSettingsLoading: 'Person Default Settings',
	orgDefaultSettingsLoading: 'Workspace Default Settings',
	locksLoading: 'Locks Permissions',
	bookingLoading: 'Bookings'
};
const UserLoading = () => {
	const dispatch = useDispatch();
	const {
		loginLoading,
		getProfileLoading,
		error: authError,
		profileLoadingComplete
	} = useSelector((state) => state.Auth);
	const {
		organisationLoading,
		error: workspaceError,
		organisationLoadingComplete
	} = useSelector((state) => state.Organisations);
	const {
		orgSettingsLoading,
		personSettingsLoading,
		error: settingsError,
		orgSettingsLoadingComplete,
		personSettingsLoadingComplete
	} = useSelector((state) => state.Settings);
	const {
		personDefaultSettingsLoading,
		orgDefaultSettingsLoading,
		orgDefaultSettingsLoadingComplete,
		personDefaultSettingsLoadingComplete,
		error: defaultSettingsError
	} = useSelector((state) => state.DefaultSettings);

	const [currentAppState, setCurrentAppState] = useState('');
	const [errorState, setErrorState] = useState('');
	// useEffect to check if the app is stuck in a loading state for 15 seconds
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (
				loginLoading ||
				getProfileLoading ||
				organisationLoading ||
				orgSettingsLoading ||
				personSettingsLoading ||
				personDefaultSettingsLoading ||
				orgDefaultSettingsLoading
			) {
				setErrorState('Loading is taking longer than expected. Please check your internet connection.');
			}
		}, 15000);

		return () => clearTimeout(timeout);
	}, [
		loginLoading,
		getProfileLoading,
		organisationLoading,
		orgSettingsLoading,
		personSettingsLoading,
		personDefaultSettingsLoading,
		orgDefaultSettingsLoading
	]);
	// useEffect to check if the app is stuck in a loading state
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (
				!profileLoadingComplete ||
				!organisationLoadingComplete ||
				!orgSettingsLoadingComplete ||
				!personSettingsLoadingComplete ||
				!personDefaultSettingsLoadingComplete ||
				!orgDefaultSettingsLoadingComplete
			) {
				// dispatch the actions to load the data again
				if (!profileLoadingComplete) dispatch(authActions.getUserProfile());
				if (!organisationLoadingComplete) dispatch(organisationActions.loadOrganisations());
				if (!orgSettingsLoadingComplete) dispatch(settingsActions.loadOrgSettings());
				if (!personSettingsLoadingComplete) dispatch(settingsActions.loadPersonSettings());
				if (!personDefaultSettingsLoadingComplete) dispatch(defaultSettingsActions.loadPersonDefaultSettings());
				if (!orgDefaultSettingsLoadingComplete) dispatch(defaultSettingsActions.loadOrgDefaultSettings());
			}
		}, 30000);

		return () => clearTimeout(timeout);
	}, [
		profileLoadingComplete,
		organisationLoadingComplete,
		orgSettingsLoadingComplete,
		personSettingsLoadingComplete,
		personDefaultSettingsLoadingComplete,
		orgDefaultSettingsLoadingComplete,
		dispatch
	]);

	useEffect(() => {
		// Check error states first and prioritize showing errors over loading
		if (authError) {
			setErrorState(authError);
		} else if (workspaceError) {
			setErrorState(workspaceError);
		} else if (settingsError) {
			setErrorState(settingsError);
		} else if (defaultSettingsError) {
			setErrorState(defaultSettingsError);
		} else {
			setErrorState('');
		}
	}, [authError, workspaceError, settingsError, defaultSettingsError]);

	useEffect(() => {
		// Check loading states in a prioritized order and set only one at a time
		if (!errorState) {
			// Only check loading states if there are no errors
			if (loginLoading) {
				setCurrentAppState(appStateInfoMap.loginLoading);
			} else if (getProfileLoading) {
				setCurrentAppState(appStateInfoMap.profileLoading);
			} else if (organisationLoading) {
				setCurrentAppState(appStateInfoMap.organisationLoading);
			} else if (orgDefaultSettingsLoading) {
				setCurrentAppState(appStateInfoMap.orgDefaultSettingsLoading);
			} else if (personDefaultSettingsLoading) {
				setCurrentAppState(appStateInfoMap.personDefaultSettingsLoading);
			} else if (orgSettingsLoading) {
				setCurrentAppState(appStateInfoMap.orgSettingsLoading);
			} else if (personSettingsLoading) {
				setCurrentAppState(appStateInfoMap.personSettingsLoading);
			} else {
				setCurrentAppState('');
			}
		}
	}, [
		loginLoading,
		getProfileLoading,
		organisationLoading,
		personDefaultSettingsLoading,
		orgDefaultSettingsLoading,
		orgSettingsLoading,
		personSettingsLoading,
		errorState
	]);

	return (
		<Row style={{ height: '100vh' }}>
			<Loader
				subTitle={
					errorState ? `${errorState}` : currentAppState ? `Loading ${currentAppState}...` : 'Loading...'
				}
				isError={errorState}
			/>
		</Row>
	);
};

export default UserLoading;
